import { notification as antdNotification } from "antd";
import NotificationCard from "components/v2/Sidebar/BottomNavigationBar/Notifications/NotificationCard";
import { NOTIFICATION_TYPES } from "constants/v2/notifications";
import { HighlightCreationContext } from "context/HighlightCreation/context";
import { useCallback, useContext, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { addNewNotification } from "store/core/notifications/actions";
import { updateNotification } from "store/core/notifications/slice";
import { selectSelectedEntityId } from "store/entity/selectors";

import { SocketContext } from "../../../../context/socket";
import styles from "./style.module.css";

const MAX_VISIBLE_NOTIFICATIONS = 2;

const CANCELLED_HL_NOTIFICATION_TYPES = [NOTIFICATION_TYPES.HIGHLIGHT, NOTIFICATION_TYPES.COPILOT];

const WebSocketNotifications = ({ entityId, addNewNotification, updateNotification }) => {
  const { webhookSocket } = useContext(SocketContext);
  const { setHighlights } = useContext(HighlightCreationContext);
  const [api, contextHolder] = antdNotification.useNotification();
  const openNotificationsRef = useRef([]);
  const timeoutRefs = useRef(new Map());

  // explicitly close the notification after 2 seconds
  const closeNotification = useCallback((key) => {
    const existingTimeout = timeoutRefs.current.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeoutId = setTimeout(() => {
      antdNotification.close(key);
      timeoutRefs.current.delete(key);
    }, 2000);

    timeoutRefs.current.set(key, timeoutId);
  }, []);

  const handleNotificationClose = useCallback((key) => {
    const existingTimeout = timeoutRefs.current.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    openNotificationsRef.current = openNotificationsRef.current.filter((k) => k !== key);
  }, []);

  const openNotification = useCallback(
    (key, content) => {
      if (openNotificationsRef.current.length >= MAX_VISIBLE_NOTIFICATIONS) {
        const oldestKey = openNotificationsRef.current.shift();

        antdNotification.close(oldestKey);
      }

      openNotificationsRef.current.push(key);

      api.open({
        key,
        message: null,
        description: content,
        duration: 2,
        placement: "topRight",
        onClose: () => {
          handleNotificationClose(key);
        },
        className: styles.toastNotificationContainer,
      });
      closeNotification(key);
    },
    [api, closeNotification, handleNotificationClose],
  );

  const closeHLGenerationNotification = useCallback((clipListId) => {
    antdNotification.close(clipListId);
  }, []);

  const handleCancelledHLNotification = useCallback(
    (statusChange) => {
      const isCancelledHL =
        CANCELLED_HL_NOTIFICATION_TYPES.includes(statusChange?.type) && statusChange?.status === "cancelled";
      if (!isCancelledHL) return;

      const pageUrl = statusChange?.metadata?.pageUrl;
      const match = pageUrl?.match(/\/quick-highlight\/([^/?]+)/);
      const clipListId = match?.[1];

      if (clipListId) {
        closeHLGenerationNotification(clipListId);
        setHighlights((prev) => prev?.map((h) => (h.clipListId === clipListId ? { ...h, status: "CANCELLED" } : h)));
      } else {
        setHighlights((prev) => {
          prev?.forEach((h) => closeHLGenerationNotification(h.clipListId));

          return prev?.map((h) => ({ ...h, status: "CANCELLED" }));
        });
      }
    },
    [closeHLGenerationNotification, setHighlights],
  );

  const handleNotifications = useCallback(
    async (data) => {
      const notificationData = data?.data || {};
      const { newNotification, progressUpdate, statusChange } = notificationData;

      if (
        newNotification?.type === NOTIFICATION_TYPES.DYNAMICFLIP ||
        progressUpdate?.type === NOTIFICATION_TYPES.DYNAMICFLIP ||
        statusChange?.type === NOTIFICATION_TYPES.DYNAMICFLIP
      ) {
        return;
      }

      if (newNotification) {
        try {
          await addNewNotification(newNotification);
          openNotification(newNotification.id, <NotificationCard notificationId={newNotification.id} isToast={true} />);
        } catch (error) {
          console.error("Failed to add notification:", error);
        }
      }

      if (progressUpdate) {
        updateNotification(progressUpdate);
      }

      if (statusChange) {
        updateNotification(statusChange);
        openNotification(statusChange.id, <NotificationCard notificationId={statusChange.id} isToast={true} />);
        handleCancelledHLNotification(statusChange);
      }
    },
    [addNewNotification, updateNotification, openNotification, handleCancelledHLNotification],
  );

  useEffect(() => {
    const currentTimeoutRefs = timeoutRefs.current;

    webhookSocket.on(`core_notifications_${entityId}`, handleNotifications);

    return () => {
      webhookSocket.off(`core_notifications_${entityId}`, handleNotifications);
      currentTimeoutRefs.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      currentTimeoutRefs.clear();
    };
  }, [handleNotifications, webhookSocket, entityId]);

  return <>{contextHolder}</>;
};

const mapStateToProps = createStructuredSelector({
  entityId: selectSelectedEntityId,
});

const mapDispatchToProps = {
  addNewNotification,
  updateNotification,
};

export default connect(mapStateToProps, mapDispatchToProps)(WebSocketNotifications);
